var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { ScreenContainer, useTheme } from '@pinwheel/origami';
import { useAppDispatch, useAppSelector } from '../../state';
import { selectClientHostname, selectFeatureFlags, selectLinkTokenPayload, selectMode, } from '../../state/slices/slice-link-params';
import { isAdp } from '../../consts/platforms';
import { selectAdpAuth, selectAdpDirectHasFailed, selectAdpDirectOTPSucceeded, setAdpDirectConnected, setAdpDirectHasFailed, setAdpDirectOTPSucceeded, setAdpDirectSwitchSucceeded, } from '../../state/slices/slice-adp-direct';
import { getHubEnvironment, isStaging } from '../../clients/hub-api/utils';
import { setError } from '../../state/slices/slice-error';
import { useOpts } from '../../context';
import { ERRORS } from '../../consts/errors';
import { AdpStatusCode } from './types';
import { clearLinkApiResponses, initializeDDS, next, selectAccountId, selectPlatformId, } from '../../state/slices/slice-link-api';
import { IS_LOCAL } from '../../consts/is-local';
import useInputRequired from '../../hooks/useInputRequired';
import useFireClientEvent from '../../hooks/useFireClientEvent';
import { updateEventSource } from '../../state/slices/slice-segment';
import { EventAction, EventSurface } from '../../types/events';
import eventQueue from '../../events/queue';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import useDetectKeyboardOpen from '../../hooks/useDetectKeyboardOpen';
import ContentFrame from '../../LinkApiRouter/components/ContentFrame';
import AdpLoading from './AdpLoading';
import { createInputAllocationPayload } from '../../events/utils';
import { LINK_API_URL } from '../../LinkApiRouter/utils/link-api-requests';
var WEBHOOK_URL = isStaging()
    ? 'https://staging.liaison.getpinwheel.com/adp-dde'
    : 'https://liaison.getpinwheel.com/adp-dde';
var PINWHEEL_URLS = ['getpinwheel.com', 'pinwheelapi.com'];
function useAdpTokenExpired(loadedContents) {
    var navigate = useLinkNavigate();
    var dispatch = useAppDispatch();
    var _a = useState(false), hasHitTimeLimit = _a[0], setHasHitTimeout = _a[1];
    var _b = useState(false), timedOutWithNoLoad = _b[0], setTimedOutWithNoLoad = _b[1];
    useEffect(function () {
        setTimeout(function () { return setHasHitTimeout(true); }, 30000);
    }, []);
    useEffect(function () {
        if (hasHitTimeLimit && !loadedContents) {
            setTimedOutWithNoLoad(true);
            datadogLogs.logger.warn('adp_failed_to_open_webpage');
            dispatch(setError(ERRORS.timedOutWithNoInput));
            navigate('/error');
        }
    }, [dispatch, hasHitTimeLimit, loadedContents, navigate]);
    return timedOutWithNoLoad;
}
/**
 * Hook to scroll to the top of the MFA screen. This is super brittle because it
 * makes UI assumptions for the ADP iframe blackbox. Importantly, this logic assumes
 * that the first screen that involves input is the MFA screen.
 *
 * This needs to be addressed to appease Acorns.
 */
function useMfaScreenScrollTop() {
    var hasScrolledToTopOnce = useRef(false);
    var shouldScrollTop = useDetectKeyboardOpen();
    useEffect(function () {
        datadogLogs.logger.info("adp_scroll_test shouldScrollTop=(".concat(shouldScrollTop, ") hasScrolledToTopOnce=(").concat(hasScrolledToTopOnce.current, ")"));
        if (shouldScrollTop && !hasScrolledToTopOnce.current) {
            hasScrolledToTopOnce.current = true;
            datadogLogs.logger.info("adp_scroll_test should_scroll");
        }
    }, [shouldScrollTop]);
}
var AdpIframe = function () {
    var opts = useOpts();
    var exit = opts.exit, fireProperEventType = opts.fireProperEventType;
    var mode = useAppSelector(selectMode);
    var clientHostname = useAppSelector(selectClientHostname);
    var hasFailed = useAppSelector(selectAdpDirectHasFailed);
    var _a = useState(false), loadedContents = _a[0], setLoadedContents = _a[1];
    var _b = useAppSelector(selectAdpAuth), accessSessionToken = _b.accessSessionToken, jwtToken = _b.jwtToken;
    var dispatch = useAppDispatch();
    var fireClientEvent = useFireClientEvent();
    var accountId = useAppSelector(selectAccountId);
    var platformId = useAppSelector(selectPlatformId);
    var navigate = useLinkNavigate();
    var otpSucceeded = useAppSelector(selectAdpDirectOTPSucceeded);
    var _c = useState(false), hasHitMaxLoadTime = _c[0], setHasHitMaxLoadTime = _c[1];
    var featureFlags = useAppSelector(selectFeatureFlags);
    var linkTokenPayload = useAppSelector(selectLinkTokenPayload);
    var _d = useState(null), initialRenderTime = _d[0], setInitialRenderTime = _d[1];
    var isDarkMode = useTheme().isDarkMode;
    useEffect(function () {
        setInitialRenderTime(Date.now());
    }, []);
    useEffect(function () {
        setTimeout(function () { return setHasHitMaxLoadTime(true); }, 3000);
    });
    var timedOutWithNoLoad = useAdpTokenExpired(loadedContents);
    useEffect(function () {
        datadogLogs.logger.info("adp_scroll_test register_parent"); // eslint-disable-line
    }, []);
    useMfaScreenScrollTop();
    /**
     * [Dec 12, 2023]
     * ADP says: "We have enhanced "parentHostUrl" so you can include as many
     * domains/URLs as needed, as long as each is separated by a space. An example
     * is: "*.example.com transact.atncdev.com".
     * Both domains with schemes and without scheme are allowed.. Whatever values
     * passed in this input , will be extracted and used in frame-ancestors as it
     * is.. We may not need trailing slash and protocol scheme with domain is suffice
     * in most cases.
     * http://*.example.com: Matches all attempts to load from any subdomain of example.com
     * using the http: URL scheme.
     * mail.example.com:443: Matches all attempts to access port 443 on mail.example.com.
     * https://store.example.com: Matches all attempts to access store.example.com using https:."
     *
     * About `parentHostUrl`, ADP says: "Since this is optional, it is not
     *  required to be included in the request all the time. When it appears
     *  in the request, the validation is expecting it to be a string. We are
     *  not able to validate if this string is a valid URL or not so please
     * include additional check in your side when assigning the value to this
     * attribute."
     */
    var parentHostUrls = useMemo(function () {
        var urls = PINWHEEL_URLS.map(function (x) { return "*.".concat(x); });
        if (!clientHostname)
            return urls;
        // Skip adding `clientHostname` if it is a Pinwheel URL.
        // We don't know how ADP will handle redundant URLs.
        if (PINWHEEL_URLS.some(function (x) { return clientHostname.includes(x); }))
            return urls;
        // Remove trailing slash
        return urls.concat(clientHostname.replace(/\/$/, ''));
    }, [clientHostname]);
    var adpReqBody = useMemo(function () {
        return {
            sessionUUID: accessSessionToken,
            JWToken: jwtToken,
            statusPostUrl: WEBHOOK_URL,
            parentHostUrl: parentHostUrls.join(' '),
            theme: isDarkMode ? 'dark' : 'light',
        };
    }, [accessSessionToken, jwtToken, parentHostUrls, isDarkMode]);
    useInputRequired(loadedContents);
    var iframeUrl = useMemo(function () {
        var qps = {
            stringifiedBody: encodeURIComponent(JSON.stringify(__assign({}, adpReqBody))),
            env: getHubEnvironment(),
            isLocal: IS_LOCAL,
            mode: mode,
        };
        var qpString = Object.entries(qps)
            .map(function (_a) {
            var k = _a[0], v = _a[1];
            return "".concat(k, "=").concat(v);
        })
            .join('&');
        var url = "".concat(LINK_API_URL, "/v1/link/ui/adp/form-redirect");
        return "".concat(url, "?").concat(qpString);
    }, [adpReqBody, mode]);
    // TODO: remove this once we understand what's happening for SESLOC ADP calls
    useEffect(function () {
        if ((linkTokenPayload === null || linkTokenPayload === void 0 ? void 0 : linkTokenPayload.workspaceName) === 'SESLOC') {
            datadogLogs.logger.info("SESLOC: ADP iframe URL = ".concat(iframeUrl));
        }
    }, [iframeUrl, linkTokenPayload === null || linkTokenPayload === void 0 ? void 0 : linkTokenPayload.workspaceName]);
    var fireSuccessEvent = useCallback(function () {
        fireClientEvent('success', {
            accountId: accountId,
            platformId: platformId,
            job: 'direct_deposit_switch',
        });
    }, [accountId, fireClientEvent, platformId]);
    var fireNecessaryAllocationClientEvents = useCallback(function () {
        // TODO: CORE-1515 remove user-entered PII experiment
        // This is for the user input PII experiment, where we are rolling out Prime to the ADPPortal platform (isAdp function checks this)
        // This means we need to support Prime on old SDKs, to do so we will mark switches as remainder switch on ADPPortal.
        // To read more see:
        // https://www.notion.so/pinwheelapi/Prime-User-Entered-PII-447220861e6744c0b4d0a043df67a922?pvs=4#c7c7b56d7cce496b9dbf6841bdaf21e0
        if (isAdp(platformId)) {
            fireClientEvent('input_amount', { value: 100, unit: '%' });
            fireClientEvent('input_allocation', createInputAllocationPayload({
                action: 'partial_switch',
                allocation: { type: 'remainder' },
            }));
        }
        else {
            fireClientEvent('input_allocation', {
                action: null,
                allocation: null,
            });
        }
    }, [fireClientEvent, platformId]);
    var messageEventListener = useCallback(function (event) { return __awaiter(void 0, void 0, void 0, function () {
        var _a, statusCode, statusMessage, loadTime;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    // For local/staging auditing purposes, log ADP's messages
                    // eslint-disable-next-line no-console
                    if (process.env.ENV !== 'production')
                        console.log('ADP:', event.data);
                    _a = event.data, statusCode = _a.statusCode, statusMessage = _a.statusMessage;
                    datadogLogs.logger.info("adp_iframe_message (".concat(statusCode, " - ").concat(statusMessage, ")"), { messageData: event.data });
                    if (timedOutWithNoLoad)
                        return [2 /*return*/];
                    if (!(statusCode === AdpStatusCode.LOADED)) return [3 /*break*/, 1];
                    // if load time is fast, wait an extra second to allow user to see loading text
                    if (!hasHitMaxLoadTime) {
                        setTimeout(function () { return setLoadedContents(true); }, 1000);
                    }
                    else {
                        setLoadedContents(true);
                    }
                    // initialRenderTime is set in useEffect, should be set by now
                    if (initialRenderTime) {
                        loadTime = Date.now() - initialRenderTime;
                        datadogLogs.logger.info("loaded iframe in ".concat(loadTime, "ms"), {
                            loadTime: loadTime,
                        });
                    }
                    return [3 /*break*/, 20];
                case 1:
                    if (!(statusCode === AdpStatusCode.SUCCESS)) return [3 /*break*/, 3];
                    fireNecessaryAllocationClientEvents();
                    dispatch(setAdpDirectConnected());
                    dispatch(setAdpDirectSwitchSucceeded());
                    dispatch(setAdpDirectHasFailed(false));
                    return [4 /*yield*/, dispatch(
                        // We are passing "full_switch" as dummy data, this is not going to get through link-api.
                        initializeDDS({ amountSelection: { action: 'full_switch' } }))];
                case 2:
                    _b.sent();
                    fireSuccessEvent();
                    fireProperEventType(EventAction.CONTINUE, opts);
                    return [3 /*break*/, 20];
                case 3:
                    if (!(statusCode === AdpStatusCode.PARTIAL_SUCCESS)) return [3 /*break*/, 5];
                    // TODO: Ask product what to do in this scenario
                    fireNecessaryAllocationClientEvents();
                    dispatch(setAdpDirectConnected());
                    return [4 /*yield*/, dispatch(
                        // We are passing "full_switch" as dummy data, this is not going to get through link-api.
                        initializeDDS({ amountSelection: { action: 'full_switch' } }))];
                case 4:
                    _b.sent();
                    return [3 /*break*/, 20];
                case 5:
                    if (!(statusCode === AdpStatusCode.FAIL ||
                        statusCode === AdpStatusCode.FAIL_NO_ALLOCATION_CHANGES)) return [3 /*break*/, 8];
                    dispatch(setError(ERRORS.systemError));
                    fireNecessaryAllocationClientEvents();
                    dispatch(setAdpDirectHasFailed(true));
                    if (!(statusCode === AdpStatusCode.FAIL_NO_ALLOCATION_CHANGES)) return [3 /*break*/, 7];
                    return [4 /*yield*/, dispatch(
                        // We are passing "full_switch" as dummy data, this is not going to get through link-api.
                        initializeDDS({ amountSelection: { action: 'full_switch' } }))];
                case 6:
                    _b.sent();
                    _b.label = 7;
                case 7: return [3 /*break*/, 20];
                case 8:
                    if (!(statusCode === AdpStatusCode.EXPIRE)) return [3 /*break*/, 11];
                    dispatch(setError(ERRORS.timedOutWithNoInput));
                    if (!hasFailed) return [3 /*break*/, 10];
                    return [4 /*yield*/, dispatch(
                        // We are passing "full_switch" as dummy data, this is not going to get through link-api.
                        initializeDDS({ amountSelection: { action: 'full_switch' } }))];
                case 9:
                    _b.sent();
                    _b.label = 10;
                case 10:
                    exit();
                    return [3 /*break*/, 20];
                case 11:
                    if (!(statusCode === AdpStatusCode.OTP_SUCCESS)) return [3 /*break*/, 13];
                    fireProperEventType(EventAction.CONTINUE, opts);
                    dispatch(setAdpDirectOTPSucceeded());
                    dispatch(updateEventSource(EventSurface.DDS_IFRAME));
                    eventQueue.emit('impression', {});
                    if (platformId) {
                        fireClientEvent('login_attempt', { platformId: platformId });
                    }
                    return [4 /*yield*/, dispatch(next({ payload: {} }))];
                case 12:
                    _b.sent();
                    return [3 /*break*/, 20];
                case 13:
                    if (!(statusCode === AdpStatusCode.CANCEL)) return [3 /*break*/, 16];
                    // Need to reset link-api response so that we don't try to come back to iframe
                    dispatch(clearLinkApiResponses());
                    if (!hasFailed) return [3 /*break*/, 15];
                    return [4 /*yield*/, dispatch(
                        // We are passing "full_switch" as dummy data, this is not going to get through link-api.
                        initializeDDS({ amountSelection: { action: 'full_switch' } }))];
                case 14:
                    _b.sent();
                    _b.label = 15;
                case 15:
                    if (otpSucceeded) {
                        exit();
                    }
                    else if (featureFlags.hideReturnToSearchButton) {
                        exit();
                    }
                    else {
                        navigate('/return_to_search');
                    }
                    return [3 /*break*/, 20];
                case 16:
                    if (!(statusCode === AdpStatusCode.END)) return [3 /*break*/, 19];
                    if (!hasFailed) return [3 /*break*/, 18];
                    return [4 /*yield*/, dispatch(
                        // We are passing "full_switch" as dummy data, this is not going to get through link-api.
                        initializeDDS({ amountSelection: { action: 'full_switch' } }))];
                case 17:
                    _b.sent();
                    _b.label = 18;
                case 18:
                    exit();
                    return [3 /*break*/, 20];
                case 19:
                    if (statusCode &&
                        !Object.values(AdpStatusCode).includes(statusCode)) {
                        datadogLogs.logger.error("unregistered_adp_event (".concat(statusCode, ")"));
                    }
                    _b.label = 20;
                case 20: return [2 /*return*/];
            }
        });
    }); }, [
        timedOutWithNoLoad,
        hasHitMaxLoadTime,
        fireNecessaryAllocationClientEvents,
        dispatch,
        fireSuccessEvent,
        fireProperEventType,
        opts,
        hasFailed,
        exit,
        platformId,
        fireClientEvent,
        otpSucceeded,
        featureFlags.hideReturnToSearchButton,
        navigate,
        initialRenderTime,
    ]);
    useEffect(function () {
        window.addEventListener('message', messageEventListener);
        // cleanup
        return function () { return window.removeEventListener('message', messageEventListener); };
    }, [messageEventListener]);
    var loadingContentLoader = useMemo(function () { return (loadedContents ? React.createElement(React.Fragment, null) : React.createElement(AdpLoading, null)); }, [loadedContents]);
    var iframeStyle = useMemo(function () {
        var styleObj = {
            width: '100%',
            height: '100%',
            border: 'none',
        };
        // Display none if the contents are not loaded yet since
        // we don't want the iframe here to uncenter `loadingContentLoader`
        if (!loadedContents)
            styleObj.display = 'none';
        return styleObj;
    }, [loadedContents]);
    return (React.createElement(ScreenContainer, { "data-testid": "adp-iframe" },
        React.createElement(ContentFrame, { fullScreenContents: true },
            loadingContentLoader,
            React.createElement("iframe", { src: iframeUrl, style: iframeStyle }))));
};
export default AdpIframe;
